import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { da } from 'date-fns/locale';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../../../../../_base-shared/models/Case/Case';
import { Note, NoteType } from '../../../../../../../../../_base-shared/models/Note';
import { UploadService } from '../../../../../app-file/upload.service';
import { NoteService } from '../../../../note.service';
import { User } from '../../../../../../../../../_base-shared/models/User/User';
import { MainGlobalEventService } from '../../../../../../_shared/services/main-global-event.service';

@Component({
  standalone: false,
  selector:    'app-case-notes',
  templateUrl: './case-notes.component.html',
  styles:      [
    `
              .scroll-notes {
                max-height: 500px;
                overflow: auto;
              }
            `],
})
export class CaseNotesComponent implements OnInit, OnDestroy {
  @Input() case: Case;
  @Input() noteType: 'note' | 'payment' | 'draft' | 'distribution';
  @Input() caseCreditorId: number;
  @Input() draftNoteType: 'case' | 'case_creditor';

  public notes: Array<Note>                  = [];
  public isLoading                           = 0;
  public selectedNoteId: number;
  public editorType: 'create' | 'edit' | null;
  public isSubmitting                        = false;
  public isNoteSticky: boolean;
  public form: UntypedFormGroup;
  public authUser: User;
  public quillModules                        = {
    imageUploader: {
      upload: (file) => this.uploadFile(file),
    },
  };
  private formObservable: Observable<LaravelResourceResponse<Note>>;
  private subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private uploadService: UploadService,
    private noteService: NoteService,
    private globalEventsService: MainGlobalEventService
  ) {
  }

  ngOnInit(): void {
    this.fetchNotes();
    this.subscriptions.push(
      this.globalEventsService.authUser$.subscribe(user => this.authUser = user)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public submitForm(form: UntypedFormGroup, saveType: NoteType): void {
    if (form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const noteData = form.value;
    noteData.type  = saveType;
    noteData.case_creditor_id = this.draftNoteType === 'case_creditor' ? this.caseCreditorId : null;

    this.isSubmitting   = true;
    this.formObservable = this.editorType === 'edit' ?
      this.noteService.update(this.selectedNoteId, noteData, ['user']) :
      this.noteService.store(this.case.id, noteData, ['user']);

    this.subscriptions.push(
      this.formObservable.pipe(finalize(() => this.isSubmitting = false)).subscribe({
        next: result => {
          const resultNote = result.data;

          if (this.editorType === 'edit') {
            const noteIndex = this.notes.findIndex(fNote => fNote.id === this.selectedNoteId);
            if (noteIndex > -1) {
              this.notes.splice(noteIndex, 1, resultNote);
            } else {
              this.notes.unshift(result.data); // todoish: add local sorting
            }
          } else {
            this.notes.unshift(result.data); // todoish: add local sorting
          }
          this.fetchNotes();
          this.editorType = null;
          this.form       = null;
        },
        error: error => console.error(error)
      }),
    );
  }

  public deleteNote(noteId: number): void {
    this.subscriptions.push(
      this.noteService.delete(noteId).pipe(finalize(() => this.isSubmitting = false)).subscribe({
        next: res => {
          const noteIndex = this.notes.findIndex(fNote => fNote.id === noteId);
          if (noteIndex > -1) {
            this.notes.splice(noteIndex, 1);
          }
          this.toastr.success(this.translate.instant('CASES.single.general.relation_list.notes.result.note_deleted'));
        },
        error: err => this.toastr.error(this.translate.instant('SHARED.went-wrong')),
      })
    );
  }

  public addNewNote(): void {
    const defaultNote = this.buildDefaultNote();
    this.buildForm(defaultNote);
  }

  public editNote(note: Note): void {
    this.selectedNoteId = note.id;
    this.form           = null;
    this.buildForm(note);
  }

  public toogleSticky(note: Note): void {
    this.isNoteSticky = !note.sticky;
    this.subscriptions.push(
      this.noteService.unsticky(note.case_id, note.id, this.isNoteSticky).subscribe({
        next: res => {
          this.toastr.success(this.translate.instant('CASES.single.general.relation_list.notes.successfully_updated_note'));
          this.fetchNotes();
        },
        error: err => this.toastr.error(this.translate.instant('SHARED.went-wrong')),
      })
    );
  }

  public cancelNote(): void {
    this.editorType     = null;
    this.form           = null;
    this.selectedNoteId = null;
  }

  public clearDatePicker(): void {
    this.form.patchValue({contacted_at: null});
  }

  private buildForm(note: Note): void {
    this.editorType = note.id ? 'edit' : 'create';
    this.form       = this.fb.group({
      type:                  [note.type, [Validators.required]],
      note:                  [note.note, [Validators.required]],
      contacted_at:          [note.contacted_at ? new Date(note.contacted_at) : null],
      customer_contact_type: [note.customer_contact_type],
    });
  }

  private fetchNotes(): void {
    const data = {
      case_creditor_id: this.caseCreditorId ? this.caseCreditorId : null,
      type: this.noteType
    };

    this.isLoading++;
    this.subscriptions.push(
      this.noteService.index(this.case.id, data, ['user', 'case_creditor']).pipe(finalize(() => this.isLoading--)).subscribe({
        next:  res => this.notes = res.data,
        error: err => console.error(err),
      })
    );
  }

  private buildDefaultNote(): Note {
    const note = new Note();
    note.type  = this.noteType;

    return note;
  }

  private uploadFile(file: any): Promise<unknown> {
    return this.uploadService.quillImgUpload(file);
  }
}
